/**
 * Lightweight Markdown-to-HTML converter for AI-generated job postings.
 * Handles the structured subset our AI is instructed to produce:
 *   ## Heading, ### Heading, **bold**, - bullet list items, blank-line paragraphs.
 *
 * NOT a general-purpose renderer — intentionally minimal, zero dependencies.
 */
export function markdownToHtml(markdown: string): string {
    if (!markdown) return ""

    const lines = markdown.split("\n")
    const html: string[] = []
    let inList = false

    for (let i = 0; i < lines.length; i++) {
        let line = lines[i]

        // Inline: **bold**
        line = line.replace(/\*\*(.+?)\*\*/g, "<strong>$1</strong>")

        // H2 heading
        if (line.startsWith("## ")) {
            if (inList) { html.push("</ul>"); inList = false }
            html.push(`<h2 class="text-base font-semibold text-gray-900 mt-4 mb-1">${line.slice(3)}</h2>`)
            continue
        }

        // H3 heading
        if (line.startsWith("### ")) {
            if (inList) { html.push("</ul>"); inList = false }
            html.push(`<h3 class="text-sm font-semibold text-gray-800 mt-3 mb-1">${line.slice(4)}</h3>`)
            continue
        }

        // Bullet list item
        if (line.match(/^[-*] /)) {
            if (!inList) { html.push('<ul class="list-disc list-inside space-y-1 text-sm text-gray-600 mb-2">'); inList = true }
            html.push(`<li>${line.slice(2)}</li>`)
            continue
        }

        // Close list on non-bullet line
        if (inList) { html.push("</ul>"); inList = false }

        // Empty line → skip (spacing handled by CSS)
        if (line.trim() === "") continue

        // Regular paragraph
        html.push(`<p class="text-sm text-gray-600 leading-relaxed mb-2">${line}</p>`)
    }

    if (inList) html.push("</ul>")

    return html.join("\n")
}

/**
 * Strips all Markdown syntax from a string and returns plain text.
 * Used for short previews (e.g., job card 2-line preview) where rendered
 * Markdown would look messy with line-clamp.
 */
export function stripMarkdown(markdown: string): string {
    if (!markdown) return ""
    return markdown
        .replace(/^#{1,3}\s+/gm, "")     // remove headings
        .replace(/\*\*(.+?)\*\*/g, "$1") // remove bold
        .replace(/^[-*] /gm, "")         // remove bullet markers
        .replace(/\n{2,}/g, " ")         // collapse blank lines to space
        .replace(/\n/g, " ")             // collapse single newlines
        .trim()
}
